<script setup lang="ts">
defineProps({
  columns: {
    required: true,
    type: Array as PropType<SchemaTableColumn[]>,
  },
  loadableColumns: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  loadingText: {
    default: 'loading',
    type: String,
  },
  maxLoading: {
    default: 4000,
    type: Number,
  },
  minLoading: {
    default: 1500,
    type: Number,
  },
  rows: {
    required: true,
    type: Array as PropType<SchemaTableRow[]>,
  },
})

const { t } = useI18n()
</script>

<template>
  <div class="gradient-texture-reverse w-full rounded-xl border border-gray-200 dark:border-gray-800">
    <div class="flex select-none items-center">
      <div
        v-for="column in columns"
        :key="column.key"
        class="bg-texture-deep rounded-t-xl border-b border-gray-200 px-2.5 py-2 dark:border-gray-800"
        :class="{
          'flex-1': column.size === 1 || !column.size,
          'flex-[2]': column.size === 2,
          'flex-[3]': column.size === 3,
          'flex-[4]': column.size === 4,
        }"
      >
        <div class="flex items-center gap-1.5 text-xs">
          <UIcon
            class="size-4"
            :name="column.icon"
          />

          <span class="font-monobold">
            {{ column.title }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-for="(row, rowIndex) in rows"
      :key="rowIndex"
      class="flex items-center"
    >
      <div
        v-for="column in columns"
        :key="column.key"
        class="overflow-x-auto whitespace-nowrap px-2.5 py-2 text-sm"
        :class="{
          'flex-1': column.size === 1 || !column.size,
          'flex-[2]': column.size === 2,
          'flex-[3]': column.size === 3,
          'flex-[4]': column.size === 4,
        }"
      >
        <div
          v-if="column.key === 'name'"
          class="flex items-center gap-2"
        >
          <UAvatar
            v-if="typeof row?.image === 'string'"
            size="xs"
            :src="row.image"
          />

          <span class="font-monobold">
            {{ row.name }}
          </span>
        </div>

        <div
          v-else-if="!loadableColumns.includes(column.key)"
          class="flex items-center gap-1"
        >
          <UBadge
            v-if="!Array.isArray(row[column.key]) && typeof row?.[column.key] === 'string'"
            :color="column.badgeColor || 'indigo'"
            :label="(row[column.key] as string)"
            size="xs"
            variant="subtle"
          />

          <template v-else>
            <UBadge
              v-for="item in row[column.key]"
              :key="item"
              :color="column.badgeColor || 'indigo'"
              :label="item"
              size="xs"
              variant="subtle"
            />
          </template>
        </div>

        <AnimationRotating
          v-else
          direction="horizontal"
          :interval="Math.floor(Math.random() * (maxLoading - minLoading)) + minLoading"
          once
          :slots="['loading', 'loaded']"
        >
          <template #loading>
            <div class="flex items-center gap-1">
              <UBadge
                class="animate-pulse"
                :color="column.badgeColor || 'purple'"
                :label="loadingText === 'loading' ? t('globals.loading') : loadingText"
                size="xs"
                variant="subtle"
              />
            </div>
          </template>

          <template #loaded>
            <div class="flex items-center gap-1">
              <UBadge
                v-if="!Array.isArray(row[column.key])"
                :color="row[column.key] === 'yes' ? 'green' : row[column.key] === 'no' ? 'red' : column.badgeColor || 'indigo'"
                :icon="['yes', 'no'].includes(row[column.key] as string) ? getIcon(row[column.key] as 'no' | 'yes') : undefined"
                :label="['yes', 'no'].includes(row[column.key] as string) ? t(`globals.${row[column.key]}`) : (row[column.key] as string)"
                size="xs"
                variant="subtle"
              />

              <template v-else>
                <UBadge
                  v-for="item in row[column.key]"
                  :key="item"
                  :color="column.badgeColor || 'indigo'"
                  :label="item"
                  size="xs"
                  variant="subtle"
                />
              </template>
            </div>
          </template>
        </AnimationRotating>
      </div>
    </div>
  </div>
</template>
